// Type definitions for window extensions used by MFEs
interface WindowWithMfeConfig extends Window {
  __BASE_URL_ISPM_NHI__?: string;
  __MFE_API_BASE_URL__?: string;
}

const getApiBaseUrl = (): string => {
  // Check if running as MFE (detected by checking if hostname is loopback or pathname includes ECMv6)
  // This replicates the ispm-ui pattern where MFEs use window.location.origin/ECMv6/api
  const isRunningAsMfe =
    typeof window !== "undefined" &&
    (window.location.hostname === "loopback" ||
      window.location.pathname.startsWith("/ECMv6") ||
      window.location.pathname.startsWith("/intelligence"));

  if (isRunningAsMfe) {
    // When running as MFE, use the same pattern as ispm-ui:
    // Check if host app has set BASE_URL_ISPM_NHI (like ispm-ui does)
    if (typeof window !== "undefined") {
      const windowWithConfig = window as WindowWithMfeConfig;
      if (
        "__BASE_URL_ISPM_NHI__" in windowWithConfig &&
        typeof windowWithConfig.__BASE_URL_ISPM_NHI__ === "string" &&
        windowWithConfig.__BASE_URL_ISPM_NHI__
      ) {
        const baseUrl = windowWithConfig.__BASE_URL_ISPM_NHI__;
        // If host provides a base URL (e.g., https://loopback:3000), use it with /ECMv6/api
        return `${baseUrl}/ECMv6/api`;
      }
    }
    // Otherwise, use window.location.origin/ECMv6/api (same as ispm-ui's getBaseUrlIspmNhi)
    return `${window.location.origin}/ECMv6/api`;
  }

  // Check if host app has set a global API base URL (fallback for other MFE setups)
  if (typeof window !== "undefined") {
    const windowWithConfig = window as WindowWithMfeConfig;
    if (
      "__MFE_API_BASE_URL__" in windowWithConfig &&
      typeof windowWithConfig.__MFE_API_BASE_URL__ === "string" &&
      windowWithConfig.__MFE_API_BASE_URL__
    ) {
      const mfeApiBaseUrl = windowWithConfig.__MFE_API_BASE_URL__;
      // If host provides a full URL (e.g., https://loopback:3000), use it directly
      if (mfeApiBaseUrl.startsWith("http")) {
        return mfeApiBaseUrl;
      }
      // If host provides a path (e.g., /api-proxy), use it
      return mfeApiBaseUrl;
    }
  }

  // In development mode, always use /api-proxy (which proxies to VITE_API_BASE_URL)
  if (import.meta.env.MODE === "development") {
    return "/api-proxy";
  }

  // In production mode, prioritize VITE_API_BASE_URL if set
  // This allows using https://loopback:3000 instead of auto-generated loopback-api
  if (import.meta.env.VITE_API_BASE_URL) {
    return import.meta.env.VITE_API_BASE_URL;
  }

  // In test/Node environment (e.g. vitest), window is undefined; use a safe default
  if (typeof window === "undefined") {
    return "/api-proxy";
  }

  // Fallback: use the current domain if VITE_API_BASE_URL is not set
  return window.location.origin;
};

export const API_BASE_URL = getApiBaseUrl();

export const AGENT_REGISTRY_API_VERSION = "v2";
